/**
 * Generic CRUD table view for RushGro modules.
 * Handles: list + search + create + edit + delete + CSV export.
 * Keeps each module page tiny (<50 lines) while retaining all required buttons.
 */
import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { PageHeader, StatusBadge, EmptyState, LoadingRows, toCsv } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { TID } from "../constants/testIds";
import { Plus, Pencil, Trash2, Search, Download } from "lucide-react";
import { toast } from "sonner";

export default function CrudTable({
  title, description, endpoint, columns, formFields, canWrite = true, extraActions,
  emptyText = "No records yet.", pageSize = 25,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined, skip, limit: pageSize };
      const { data } = await api.get(endpoint, { params });
      setRows(data.items); setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip]);
  useEffect(() => { const t = setTimeout(() => { setSkip(0); load(); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const initForm = useMemo(() => {
    const o = {}; formFields.forEach((f) => { o[f.name] = f.default ?? ""; }); return o;
  }, [formFields]);

  const openCreate = () => { setEditing(null); setForm(initForm); setOpenForm(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpenForm(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`${endpoint}/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post(endpoint, form);
        toast.success("Created");
      }
      setOpenForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (r) => {
    try { await api.delete(`${endpoint}/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader
        title={title}
        description={description}
        actions={
          <>
            <Button variant="outline" data-testid={TID.exportBtn} onClick={() => toCsv(rows, `${title.toLowerCase()}.csv`)}>
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            {extraActions}
            {canWrite && (
              <Button data-testid={TID.addBtn} onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            )}
          </>
        }
      />

      <Card className="rounded-xl">
        <div className="p-3 border-b flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid={TID.searchInput} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
          </div>
          <div className="text-xs text-muted-foreground ml-auto">{total} record{total === 1 ? "" : "s"}</div>
        </div>

        {loading ? (
          <div className="p-4"><LoadingRows rows={6} /></div>
        ) : rows.length === 0 ? (
          <div className="p-4"><EmptyState title={emptyText} /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => <TableHead key={c.key} className="text-xs uppercase tracking-wider">{c.label}</TableHead>)}
                  {canWrite && <TableHead className="text-right text-xs uppercase tracking-wider">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="hover:bg-muted/40">
                    {columns.map((c) => (
                      <TableCell key={c.key} className="text-sm">
                        {c.render ? c.render(r[c.key], r) : (c.status ? <StatusBadge status={r[c.key]} /> : (r[c.key] ?? "-"))}
                      </TableCell>
                    ))}
                    {canWrite && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" data-testid={TID.editBtn(r.id)} onClick={() => openEdit(r)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={TID.deleteBtn(r.id)}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction data-testid={`confirm-delete-${r.id}`} onClick={() => remove(r)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {total > pageSize && (
          <div className="p-3 border-t flex items-center justify-between text-sm">
            <div className="text-muted-foreground">Page {Math.floor(skip / pageSize) + 1} of {Math.ceil(total / pageSize)}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - pageSize))}>Previous</Button>
              <Button variant="outline" size="sm" disabled={skip + pageSize >= total} onClick={() => setSkip(skip + pageSize)}>Next</Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${title.replace(/s$/, "")}` : `New ${title.replace(/s$/, "")}`}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {formFields.map((f) => (
              <div key={f.name} className={f.wide ? "md:col-span-2 space-y-1.5" : "space-y-1.5"}>
                <Label htmlFor={`f-${f.name}`}>{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                {f.type === "select" ? (
                  <Select value={String(form[f.name] ?? "")} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger id={`f-${f.name}`} data-testid={`form-${f.name}`}><SelectValue placeholder={f.placeholder || "Select…"} /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={`f-${f.name}`}
                    data-testid={`form-${f.name}`}
                    type={f.type || "text"}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid={TID.cancelBtn} onClick={() => setOpenForm(false)}>Cancel</Button>
            <Button data-testid={TID.saveBtn} onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
